import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, Calendar, Phone, Car, Bike, User, ShieldCheck } from "lucide-react";
import { API_BASE } from "@/lib/api";

export function getVisitorPhotoSrc(visitor) {
    if (!visitor) return null;
    const token = typeof window !== "undefined" ? localStorage.getItem("session_token") : null;
    const authQuery = token ? `?auth=${encodeURIComponent(token)}` : "";

    if (visitor.photo_url) {
        if (visitor.photo_url.startsWith("http://") || visitor.photo_url.startsWith("https://")) {
            return visitor.photo_url;
        }
        const base = `${API_BASE.replace(/\/api$/, "")}${visitor.photo_url}`;
        return `${base}${authQuery}`;
    }

    if (visitor.help_id && (visitor.has_photo || visitor.photo_path)) {
        return `${API_BASE}/daily-help/${visitor.help_id}/photo${authQuery}`;
    }

    if (visitor._isWalkin || visitor.walkin_id) {
        const id = visitor.walkin_id || visitor.visitor_id;
        return `${API_BASE}/walkins/${id}/photo${authQuery}`;
    }

    if (visitor.has_photo && visitor.visitor_id) {
        return `${API_BASE}/visitors/${visitor.visitor_id}/photo${authQuery}`;
    }

    return null;
}

export default function VisitorPhotoModal({ visitor, onClose, seniorMode = true }) {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        setImgError(false);
    }, [visitor]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    if (!visitor) return null;

    const photoSrc = getVisitorPhotoSrc(visitor);
    const dateStr = visitor.created_at || visitor.entered_at || visitor.expected_at;
    let formattedDate = "—";
    let formattedTime = "";
    if (dateStr) {
        try {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) {
                formattedDate = d.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                });
                formattedTime = d.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                });
            }
        } catch {}
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-xs p-3 sm:p-4 animate-in fade-in duration-200"
            onClick={onClose}
            data-testid="visitor-photo-modal"
        >
            <div
                className="bg-white rounded-3xl border border-nest-line w-full max-w-lg overflow-hidden shadow-2xl transition-all"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 px-6 border-b border-nest-line bg-nest-alt/40">
                    <div className="flex items-center gap-3">
                        <span className={`rounded-2xl bg-nest-clay/10 text-nest-clay grid place-items-center ${seniorMode ? "h-11 w-11" : "h-8 w-8"}`}>
                            <ShieldCheck size={seniorMode ? 22 : 18} />
                        </span>
                        <div>
                            <p className={`font-black uppercase tracking-wider text-nest-clay ${seniorMode ? "text-xs" : "text-[10px]"}`}>Gate Security Verification</p>
                            <h3 className={`font-bold text-nest-ink ${seniorMode ? "text-xl sm:text-2xl" : "font-display text-lg"}`}>Visitor Photo Record</h3>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className={`grid place-items-center rounded-2xl bg-nest-alt/70 hover:bg-nest-alt text-nest-ink transition ${seniorMode ? "h-11 w-11" : "h-8 w-8"}`}
                        title="Close"
                        data-testid="close-photo-modal"
                    >
                        <X size={seniorMode ? 22 : 18} />
                    </button>
                </div>

                {/* Photo Display */}
                <div className="relative bg-neutral-900 flex items-center justify-center min-h-[280px] max-h-[380px] overflow-hidden">
                    {photoSrc && !imgError ? (
                        <img
                            src={photoSrc}
                            alt={visitor.name || "Visitor"}
                            className="w-full h-full object-contain max-h-[380px]"
                            onError={() => setImgError(true)}
                            data-testid="modal-visitor-img"
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
                            <User size={48} className="mb-2 opacity-50" />
                            <p className="text-base font-bold text-neutral-200">No photo on record</p>
                            <p className="text-xs text-neutral-400 mt-1">Pre-approved pass or photo pending</p>
                        </div>
                    )}

                    {photoSrc && !imgError && (
                        <a
                            href={photoSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-medium backdrop-blur-md transition"
                            title="Open original high-res image"
                        >
                            <ExternalLink size={12} />
                            <span>Full size</span>
                        </a>
                    )}
                </div>

                {/* Details Section */}
                <div className="p-5 sm:p-6 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className={`font-black text-nest-ink ${seniorMode ? "text-2xl sm:text-3xl" : "font-display text-2xl"}`}>{visitor.name || "Visitor"}</h2>
                                {visitor._isDailyHelp || visitor.help_id || visitor.service_type ? (
                                    <span className="text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-xl bg-rose-100 text-rose-800 border border-rose-300">
                                        Daily Staff · {visitor.service_type || "Helper"}
                                    </span>
                                ) : visitor._isWalkin ? (
                                    <span className="text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-xl bg-nest-clay/15 text-nest-clay border border-nest-clay/30">
                                        Walk-in
                                    </span>
                                ) : (
                                    <span className="text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-xl bg-emerald-100 text-emerald-800 border border-emerald-300">
                                        Pre-approved
                                    </span>
                                )}
                            </div>
                            <p className={`mt-1.5 ${seniorMode ? "text-base text-nest-ink2 font-medium" : "text-sm text-nest-ink2"}`}>
                                {visitor._isDailyHelp || visitor.help_id || visitor.service_type ? (
                                    <span>
                                        Passcode: <strong className="font-mono text-nest-ink font-bold">{visitor.pass_code || visitor.code || "—"}</strong> · Servicing: <strong className="text-nest-ink">{Array.isArray(visitor.flats) && visitor.flats.length ? visitor.flats.join(", ") : (visitor.host_flat || "Society-wide")}</strong>
                                    </span>
                                ) : (
                                    <span>
                                        {visitor.purpose || "Visitor"} · Host: <strong className="text-nest-ink bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md font-bold">{visitor.host_flat || visitor.flat || "Society"}</strong> {visitor.host_name ? `(${visitor.host_name})` : ""}
                                    </span>
                                )}
                            </p>
                        </div>

                        <span
                            className={`font-black uppercase tracking-wider rounded-xl text-center shrink-0 shadow-2xs ${
                                seniorMode ? "text-xs sm:text-sm px-3.5 py-1.5" : "text-[10px] px-2.5 py-1"
                            } ${
                                visitor.status === "entered"
                                    ? "bg-emerald-600 text-white"
                                    : visitor.status === "exited"
                                    ? "bg-nest-sand text-nest-ink border border-nest-line"
                                    : visitor.status === "approved"
                                    ? "bg-blue-600 text-white"
                                    : visitor.status === "rejected"
                                    ? "bg-nest-err text-white"
                                    : "bg-amber-500 text-white"
                            }`}
                        >
                            {visitor.status || "Logged"}
                        </span>
                    </div>

                    <div className={`grid grid-cols-2 gap-3 pt-3 border-t border-nest-line ${seniorMode ? "text-sm" : "text-xs"}`}>
                        {visitor.phone && (
                            <div className="space-y-1">
                                <span className={`text-nest-ink3 block uppercase font-bold tracking-wider ${seniorMode ? "text-xs" : "text-[10px]"}`}>Contact</span>
                                <a
                                    href={`tel:${String(visitor.phone).replace(/[^0-9+]/g, "")}`}
                                    className="inline-flex items-center gap-1.5 font-bold text-nest-ink hover:text-nest-clay"
                                >
                                    <Phone size={seniorMode ? 15 : 12} className="text-nest-clay" />
                                    <span>{visitor.phone}</span>
                                </a>
                            </div>
                        )}

                        {visitor.vehicle_no && (
                            <div className="space-y-1">
                                <span className={`text-nest-ink3 block uppercase font-bold tracking-wider ${seniorMode ? "text-xs" : "text-[10px]"}`}>Vehicle</span>
                                <span className="inline-flex items-center gap-1 font-mono font-black px-2.5 py-1 rounded-lg bg-nest-alt border border-nest-line text-nest-ink">
                                    {visitor.vehicle_type === "bike" ? <Bike size={seniorMode ? 15 : 12} className="text-nest-clay" /> : <Car size={seniorMode ? 15 : 12} className="text-nest-clay" />}
                                    <span>{visitor.vehicle_no}</span>
                                </span>
                            </div>
                        )}

                        <div className="space-y-1">
                            <span className={`text-nest-ink3 block uppercase font-bold tracking-wider ${seniorMode ? "text-xs" : "text-[10px]"}`}>Logged At</span>
                            <div className="text-nest-ink font-semibold flex items-center gap-1">
                                <Calendar size={seniorMode ? 15 : 12} className="text-nest-clay" />
                                <span>{formattedDate} {formattedTime}</span>
                            </div>
                        </div>

                        {visitor.code && (
                            <div className="space-y-1">
                                <span className={`text-nest-ink3 block uppercase font-bold tracking-wider ${seniorMode ? "text-xs" : "text-[10px]"}`}>Pass Code</span>
                                <span className="font-mono font-black text-nest-ink tracking-wider text-base">{visitor.code}</span>
                            </div>
                        )}
                    </div>

                    {/* Bottom CTA */}
                    <div className="pt-2 flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`w-full sm:w-auto rounded-2xl bg-nest-ink text-white hover:bg-neutral-800 font-bold transition cursor-pointer shadow-md ${
                                seniorMode ? "h-12 px-6 text-sm sm:text-base" : "px-5 py-2.5 text-xs"
                            }`}
                            data-testid="modal-done-btn"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
